<!--
VHF呼叫记录面板组件

使用说明：
1. 该组件基于现有的告警记录接口(alert-record.js)实现
2. 支持按类型分类显示：偏航、超高、超速、黑名单、碰撞
3. 提供搜索、分页、详情查看、快速处理等功能
4. 支持查看船舶轨迹

Props:
- visible: 控制面板显示/隐藏
- bridgeId: 桥梁ID
- zIndex: 面板层级

Events:
- update:visible: 面板显示状态变化
- panel-focus: 面板获得焦点
- show-ship-track-with-data: 显示船舶轨迹

使用示例：
<VhfCallRecordPanel
  :visible="vhfPanelVisible"
  :bridge-id="currentBridgeId"
  :z-index="1000"
  @update:visible="vhfPanelVisible = $event"
  @show-ship-track-with-data="handleShowShipTrack"
/>
-->

<template>
  <draggable-panel
    :visible="visible"
    :title="title"
    :icon="icon"
    :width="950"
    :tabs="tabs"
    :active-tab="currentActiveTab"
    :initial-position="{ top: 60 }"
    :z-index="zIndex"
    :enable-expand="true"
    :is-expanded="isExpanded"
    :expanded-width="1460"
    @close="handleClose"
    @tab-change="handleTabChange"
    @panel-focus="handlePanelFocus"
    @expand-toggle="handleExpandToggle"
  >
    <!-- 内容区域 -->
    <div class="panel-content" v-loading="loading">
      <!-- 搜索表单 -->
      <div class="search-form">
        <el-form :inline="true" :model="searchForm" class="compact-form" size="small">
          <el-row :gutter="8" type="flex" justify="start" align="middle">
            <el-col :span="6">
              <el-form-item label="MMSI">
                <el-input v-model="searchForm.sourceId" placeholder="请输入船舶MMSI" clearable size="small"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="级别">
                <el-select v-model="searchForm.alertLevel" placeholder="全部" clearable size="small">
                  <el-option 
                    v-for="item in alertLevelOptions" 
                    :key="getAlertLevelValue(item.value)" 
                    :label="item.label" 
                    :value="getAlertLevelValue(item.value)">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="状态">
                <el-select v-model="searchForm.status" placeholder="全部" clearable size="small">
                  <el-option label="未处理" :value="0"></el-option>
                  <el-option label="已处理" :value="1"></el-option>
                  <el-option label="已忽略" :value="2"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item class="search-buttons-item">
                <div class="search-buttons">
                  <el-button type="primary" size="small" @click="searchRecords" icon="el-icon-search">查询</el-button>
                  <el-button size="small" @click="resetSearchForm" icon="el-icon-refresh">重置</el-button>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>

      <!-- 记录列表 -->
      <div class="records-container">
        <el-table
          :data="currentRecords"
          border
          stripe
          style="width: 100%"
          v-loading="loading"
          size="small"
          height="300"
          fit
        >
          <el-table-column prop="id" label="编号" width="60"></el-table-column>
          <el-table-column prop="alertTime" label="告警时间" width="130">
            <template slot-scope="scope">
              {{ formatTime(scope.row.alertTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="sourceId" label="MMSI" width="90"></el-table-column>
          <el-table-column prop="alertLevel" label="级别" width="70">
            <template slot-scope="scope">
              <el-tag :type="getAlertLevelType(scope.row.alertLevel)" size="mini">{{ getAlertLevelType(scope.row.alertLevel) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="ruleType" label="类型" width="80">
            <template slot-scope="scope">
              {{ getRuleTypeText(scope.row.ruleType) }}
            </template>
          </el-table-column>
          <el-table-column prop="alertMessage" label="告警内容" min-width="120">
            <template slot-scope="scope">
              <el-tooltip :content="scope.row.alertMessage" placement="top" :disabled="!scope.row.alertMessage">
                <div class="alert-message-cell">{{ scope.row.alertMessage }}</div>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="70">
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row.status)" size="mini">{{ getStatusText(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-dropdown @command="handleCommand" trigger="click">
                <el-button size="mini" type="primary">
                  操作<i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :command="{action: 'view', row: scope.row}">
                    <i class="el-icon-view"></i> 查看
                  </el-dropdown-item>
                  <el-dropdown-item 
                    :command="{action: 'track', row: scope.row}"
                    :disabled="!scope.row.sourceId"
                  >
                    <i class="el-icon-location"></i> 轨迹
                  </el-dropdown-item>
                  <el-dropdown-item 
                    v-if="scope.row.status === 0"
                    :command="{action: 'handle', row: scope.row}"
                  >
                    <i class="el-icon-edit"></i> 处理
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
        
        <div class="pagination-container">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.pageNum"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            small
          >
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- 记录详情弹窗 -->
    <el-dialog
      title="VHF呼叫记录详情"
      :visible.sync="detailVisible"
      width="60%"
      append-to-body
      :modal-append-to-body="false"
    >
      <div v-if="currentRecord.id" class="record-detail-container">
        <div class="record-info-card">
          <!-- 基本信息 -->
          <div class="record-info-row">
            <div class="record-info-item">
              <span class="label">编号：</span>
              <span class="value">{{ currentRecord.id }}</span>
            </div>
            <div class="record-info-item">
              <span class="label">时间：</span>
              <span class="value">{{ formatTime(currentRecord.alertTime) }}</span>
            </div>
            <div class="record-info-item">
              <span class="label">类型：</span>
              <span class="value">{{ getRuleTypeText(currentRecord.ruleType) }}</span>
            </div>
            <div class="record-info-item">
              <span class="label">级别：</span>
              <span class="value">
                <el-tag :type="getAlertLevelType(currentRecord.alertLevel)" size="mini">{{ getAlertLevelType(currentRecord.alertLevel) }}</el-tag>
              </span>
            </div>
          </div>
          
          <!-- 船舶信息 -->
          <div class="record-info-row">
            <div class="record-info-item">
              <span class="label">MMSI：</span>
              <span class="value">{{ currentRecord.sourceId }}</span>
            </div>
            <div class="record-info-item">
              <span class="label">船舶名称：</span>
              <span class="value">{{ currentRecord.sourceName || '未知' }}</span>
            </div>
            <div class="record-info-item" v-if="currentRecord.longitude && currentRecord.latitude">
              <span class="label">位置：</span>
              <span class="value">{{ formatCoordinate(currentRecord.longitude, currentRecord.latitude) }}</span>
            </div>
          </div>
          
          <!-- 告警消息 -->
          <div class="record-message">
            {{ currentRecord.alertMessage }}
          </div>
          
          <!-- 处理区域 -->
          <div v-if="currentRecord.status === 0" class="record-handle-area">
            <el-divider content-position="left">记录处理</el-divider>
            <div class="handle-row">
              <div class="handle-options">
                <el-radio v-model="handleForm.status" :label="1">已处理</el-radio>
                <el-radio v-model="handleForm.status" :label="2">已忽略</el-radio>
              </div>
              <el-input v-model="handleForm.handleResult" placeholder="请输入处理结果（可选）" class="handle-input"></el-input>
              <el-button type="primary" size="small" @click="handleRecord" :loading="submitLoading">提交</el-button>
            </div>
          </div>
          
          <!-- 已处理信息 -->
          <div v-else-if="currentRecord.processor || currentRecord.handleTime" class="record-handled-info">
            <el-divider content-position="left">处理信息</el-divider>
            <div class="handle-info-row">
              <div class="info-item">
                <span class="info-label">处理人：</span>
                <span>{{ currentRecord.processor || '无' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">处理时间：</span>
                <span>{{ formatTime(currentRecord.handleTime) || '无' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">处理结果：</span>
                <span>{{ currentRecord.handleResult || '无' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">状态：</span>
                <span>
                  <el-tag :type="getStatusType(currentRecord.status)" size="mini">{{ getStatusText(currentRecord.status) }}</el-tag>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 快速处理弹窗 -->
    <el-dialog
      title="快速处理记录"
      :visible.sync="quickHandleVisible"
      width="40%"
      append-to-body
      :modal-append-to-body="false"
    >
      <el-form :model="quickHandleForm" label-width="80px">
        <el-form-item label="处理方式">
          <el-radio-group v-model="quickHandleForm.status">
            <el-radio :label="1">已处理</el-radio>
            <el-radio :label="2">已忽略</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="处理结果">
          <el-input type="textarea" v-model="quickHandleForm.handleResult" :rows="3" placeholder="请输入处理结果"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="quickHandleVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmQuickHandle" :loading="submitLoading">确定</el-button>
      </span>
    </el-dialog>
  </draggable-panel>
</template>

<script>
import { 
  getAlertRecordList, 
  handleAlertRecord
} from '@/api/alert-record';

import { getAlertLevelOptions } from '@/api/alert';
import { formatLatitude, formatLongitude } from '@/utils/coordinate';
import { formatDate } from '@/utils/dateUtils';
import DraggablePanel from '@/components/common/DraggablePanel.vue';

export default {
  name: 'VhfCallRecordPanel',
  components: {
    DraggablePanel
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bridgeId: {
      type: [Number, String],
      default: null
    },
    zIndex: {
      type: Number,
      default: 1000
    }
  },
  data() {
    return {
      title: 'VHF呼叫记录',
      icon: 'el-icon-phone',
      tabs: [
        { key: 'YAW', label: '偏航' },
        { key: 'overHeight', label: '超高' },
        { key: 'overSpeed', label: '超速' },
        { key: 'blacklist', label: '黑名单' }
      ],
      currentActiveTab: 'YAW',
      isExpanded: false, // 展开/收起状态
      currentRecords: [],
      currentRecord: {},
      detailVisible: false,
      quickHandleVisible: false,
      quickHandleRecordId: null,
      handleForm: {
        status: 1,
        handleResult: ''
      },
      quickHandleForm: {
        status: 1,
        handleResult: ''
      },
      searchForm: {
        sourceId: '',
        alertLevel: '',
        status: '',
        timeRange: null
      },
      pagination: {
        total: 0,
        pageNum: 1,
        pageSize: 20
      },
      statistics: {
        total: 0,
        YAW: 0,
        overHeight: 0,
        overSpeed: 0,
        blacklist: 0,
        collision: 0
      },
      alertLevelOptions: [],
      loading: false,
      submitLoading: false,
      // 规则类型映射 - 标签页key到API规则类型代码的映射
      ruleTypeMap: {
        'YAW': 'YAW',        // 偏航
        'overHeight': 'OVERHEIGHT', // 超高
        'overSpeed': 'OVERSPEED',   // 超速
        'blacklist': 'BLACKLIST',   // 黑名单
        'collision': 'COLLISION'    // 碰撞
      },
      // 规则类型显示文本映射 - API规则类型代码到中文显示文本的映射
      ruleTypeDisplayMap: {
        'YAW': '偏航',
        'OVERHEIGHT': '超高',
        'OVERSPEED': '超速',
        'BLACKLIST': '黑名单',
        'COLLISION': '碰撞'
      }
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.init();
      } else {
        this.resetForms();
      }
    },
    currentActiveTab(newVal) {
      this.handleTabChange(newVal);
    }
  },
  mounted() {
    // 将面板提升到 body，避免被页面内浮层层叠上下文遮挡
    if (this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el);
    }
  },
  beforeDestroy() {
    // 组件销毁时清理挂载节点
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },
  methods: {
    /**
     * 初始化
     */
    async init() {
      await this.initData();
      this.loadStatistics();
      this.searchRecords();
    },

    /**
     * 初始化数据
     */
    async initData() {
      try {
        this.loading = true;
        await this.fetchAlertLevelOptions();
      } catch (error) {
        console.error('初始化数据失败:', error);
        this.$message.error('加载数据失败，请刷新重试');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 加载统计信息
     */
    async loadStatistics() {
      const bridgeId = this.getBridgeIdAsNumber();
      if (!bridgeId) return;

      try {
        // 使用现有的告警记录接口获取统计信息
        const params = {
          bridgeId: bridgeId,
          pageSize: 1000 // 获取足够多的数据进行统计
        };
        
        const res = await getAlertRecordList(params);
        if (res.code === 200 && res.data && res.data.list) {
          const records = res.data.list;
          this.statistics = {
            total: records.length,
            YAW: records.filter(r => r.ruleType === 'YAW').length,
            overHeight: records.filter(r => r.ruleType === 'OVERHEIGHT').length,
            overSpeed: records.filter(r => r.ruleType === 'OVERSPEED').length,
            blacklist: records.filter(r => r.ruleType === 'BLACKLIST').length,
            collision: records.filter(r => r.ruleType === 'COLLISION').length
          };
        }
      } catch (error) {
        console.error('加载统计信息失败:', error);
      }
    },

    /**
     * 获取桥梁ID
     */
    getBridgeIdAsNumber() {
      if (!this.bridgeId) return null;
      return typeof this.bridgeId === 'string' ? parseInt(this.bridgeId, 10) : this.bridgeId;
    },

    /**
     * 格式化坐标显示
     */
    formatCoordinate(longitude, latitude) {
      if (!longitude || !latitude) return '未知位置';
      return `${formatLongitude(longitude)} ${formatLatitude(latitude)}`;
    },

    /**
     * 格式化时间显示
     */
    formatTime(time) {
      return formatDate(time);
    },

    /**
     * 搜索记录
     */
    async searchRecords() {
      const bridgeId = this.getBridgeIdAsNumber();
      if (!bridgeId) return;

      const params = {
        pageNum: this.pagination.pageNum,
        pageSize: this.pagination.pageSize,
        bridgeId: bridgeId,
        sourceId: this.searchForm.sourceId || undefined,
        alertLevel: this.searchForm.alertLevel || undefined,
        status: this.searchForm.status !== '' ? this.searchForm.status : undefined
      };

      // 处理时间范围
      if (this.searchForm.timeRange && this.searchForm.timeRange.length === 2) {
        params.startTimeStr = this.searchForm.timeRange[0];
        params.endTimeStr = this.searchForm.timeRange[1];
      }

      // 根据当前标签页添加规则类型过滤
      if (this.currentActiveTab !== 'all') {
        params.ruleType = this.ruleTypeMap[this.currentActiveTab];
      }

      try {
        this.loading = true;
        const res = await getAlertRecordList(params);
        if (res.code === 200) {
          this.currentRecords = res.data.list || [];
          this.pagination.total = res.data.total || 0;
        } else {
          this.$message.error(res.message || '查询记录失败');
        }
      } catch (error) {
        console.error('查询记录失败:', error);
        this.$message.error('查询记录失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 重置搜索表单
     */
    resetSearchForm() {
      this.searchForm = {
        sourceId: '',
        alertLevel: '',
        status: '',
        timeRange: null
      };
      this.pagination.pageNum = 1;
      this.searchRecords();
    },

    /**
     * 重置所有表单
     */
    resetForms() {
      this.handleForm = {
        status: 1,
        handleResult: ''
      };
      this.quickHandleForm = {
        status: 1,
        handleResult: ''
      };
    },

    /**
     * 处理标签页切换
     */
    handleTabChange(tabKey) {
      this.currentActiveTab = tabKey;
      this.pagination.pageNum = 1;
      this.searchRecords();
    },

    /**
     * 查看记录详情
     */
    viewRecordDetail(record) {
      this.currentRecord = { ...record };
      this.detailVisible = true;
    },

    /**
     * 快速处理记录
     */
    quickHandleRecord(record) {
      this.quickHandleRecordId = record.id;
      this.quickHandleVisible = true;
    },

    /**
     * 确认快速处理
     */
    async confirmQuickHandle() {
      try {
        this.submitLoading = true;
        const res = await handleAlertRecord(
          this.quickHandleRecordId,
          this.quickHandleForm.status,
          this.quickHandleForm.handleResult
        );

        if (res.code === 200) {
          this.$message.success('处理成功');
          this.quickHandleVisible = false;
          this.searchRecords();
          this.loadStatistics();
        } else {
          this.$message.error(res.message || '处理失败');
        }
      } catch (error) {
        console.error('处理记录失败:', error);
        this.$message.error('处理记录失败');
      } finally {
        this.submitLoading = false;
      }
    },

    /**
     * 处理记录
     */
    async handleRecord() {
      try {
        this.submitLoading = true;
        const res = await handleAlertRecord(
          this.currentRecord.id,
          this.handleForm.status,
          this.handleForm.handleResult
        );

        if (res.code === 200) {
          this.$message.success('处理成功');
          this.detailVisible = false;
          this.searchRecords();
          this.loadStatistics();
        } else {
          this.$message.error(res.message || '处理失败');
        }
      } catch (error) {
        console.error('处理记录失败:', error);
        this.$message.error('处理记录失败');
      } finally {
        this.submitLoading = false;
      }
    },

    /**
     * 显示船舶轨迹
     */
    async showShipTrack(record) {
      if (!record || !record.sourceId) {
        this.$message.warning('该记录缺少船舶MMSI，无法查看轨迹');
        return;
      }

      try {
        this.$message.info('正在查询船舶轨迹数据...');

        // 计算时间范围：事件前后各1小时
        const alertTime = new Date(record.alertTime);
        const startTime = new Date(alertTime.getTime() - 60 * 60 * 1000); // 1小时前
        const endTime = new Date(alertTime.getTime() + 60 * 60 * 1000); // 1小时后

        // 直接调用API获取轨迹数据
        const { searchVesselTrajectory } = await import('@/api/vessel-trajectory');
        const params = {
          keyword: record.sourceId,
          startTime: this.formatDateTime(startTime),
          endTime: this.formatDateTime(endTime),
          pageNum: 1,
          pageSize: 1000
        };

        const res = await searchVesselTrajectory(params);

        if (res.code === 200 && res.data && res.data.list) {
          this.$emit('show-ship-track-with-data', record.sourceId, {
            trajectoryData: res.data,
            alertData: record,
            timeRange: {
              startTime: this.formatDateTime(startTime),
              endTime: this.formatDateTime(endTime)
            },
            useLatestRecords: true,
            maxRecords: 10
          });
        } else {
          this.$message.error('查询轨迹数据失败：' + (res.message || '未知错误'));
        }
      } catch (error) {
        console.error('查询船舶轨迹失败:', error);
        this.$message.error('查询船舶轨迹失败，请检查网络连接');
      }
    },

    /**
     * 格式化日期时间
     */
    formatDateTime(date) {
      if (!date || !(date instanceof Date)) {
        return '';
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },

    /**
     * 处理分页大小变化
     */
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.searchRecords();
    },

    /**
     * 处理当前页变化
     */
    handleCurrentChange(page) {
      this.pagination.pageNum = page;
      this.searchRecords();
    },

    /**
     * 获取告警级别对应的类型
     */
    getAlertLevelType(level) {
      var levelKey = "INFO";
      if (level === 1) {
        levelKey = "LOW";
      } else if (level === 2) {
        levelKey = "INFO";
      } else if (level === 3) {
        levelKey = "WARNING";
      } else if (level === 4) {
        levelKey = "DANGER";
      } else if (level === 5) {
        levelKey = "CRITICAL";
      }
      var levelText = this.getAlertLevelText(levelKey);
      return levelText;
    },

    /**
     * 获取告警级别对应的值
     */
    getAlertLevelValue(level) {
      var levelValue = 2;
      if (level == "INFO") {
        levelValue = 2;
      } else if (level === "WARNING") {
        levelValue = 3;
      } else if (level === "DANGER") {
        levelValue = 4;
      } else if (level === "CRITICAL") {
        levelValue = 5;
      }
      return levelValue;
    },

    /**
     * 获取告警级别对应的文本
     */
    getAlertLevelText(level) {
      const option = this.alertLevelOptions.find(item => item.value === level);
      if (option) {
        return option.label;
      }

      switch (level) {
        case 'LOW':
          return '低';
        case 'INFO':
          return '信息';
        case 'WARNING':
          return '警告';
        case 'DANGER':
          return '危险';
        case 'CRITICAL':
          return '严重';
        default:
          return level || '未知';
      }
    },

    /**
     * 获取告警类型对应的文本
     */
    getRuleTypeText(type) {
      if (!type) return '未知';
      // 使用显示映射将英文规则类型代码转换为中文显示文本
      return this.ruleTypeDisplayMap[type] || type;
    },

    /**
     * 获取状态对应的类型
     */
    getStatusType(status) {
      switch (status) {
        case 0:
          return 'warning';
        case 1:
          return 'success';
        case 2:
          return 'info';
        default:
          return 'info';
      }
    },

    /**
     * 获取状态对应的文本
     */
    getStatusText(status) {
      switch (status) {
        case 0:
          return '未处理';
        case 1:
          return '已处理';
        case 2:
          return '已忽略';
        default:
          return '未知';
      }
    },

    /**
     * 获取告警级别选项
     */
    async fetchAlertLevelOptions() {
      try {
        const res = await getAlertLevelOptions();
        if (res.code === 200 && Array.isArray(res.data)) {
          this.alertLevelOptions = res.data.map(item => ({
            label: item.label || item.value,
            value: item.value || item.key
          }));
        } else {
          console.error('获取告警级别选项失败:', res.message || '未知错误');
        }
      } catch (error) {
        console.error('获取告警级别选项出错:', error);
      }
    },

    /**
     * 处理关闭事件
     */
    handleClose() {
      this.$emit('update:visible', false);
    },

    /**
     * 处理标签页变化
     */
    handleTabChange(tabKey) {
      this.currentActiveTab = tabKey;
      this.pagination.pageNum = 1;
      this.searchRecords();
    },

    /**
     * 处理面板聚焦
     */
    handlePanelFocus() {
      this.$emit('panel-focus');
    },
    
    /**
     * 处理展开/收起切换
     */
    handleExpandToggle(expanded) {
      this.isExpanded = expanded;
    },

    /**
     * 处理表格操作命令
     */
    handleCommand(command) {
      const { action, row } = command;
      if (action === 'view') {
        this.viewRecordDetail(row);
      } else if (action === 'track') {
        this.showShipTrack(row);
      } else if (action === 'handle') {
        this.quickHandleRecord(row);
      }
    }
  }
};
</script>

<style scoped>
/* 面板内容样式 */
.panel-content {
  padding: 0;
  max-height: 70vh;
}

/* 统计信息样式 */
.statistics-container {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 15px;
  margin: 15px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.statistics-row {
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #606266;
}

/* 搜索表单样式 */
.search-form {
  margin: 15px 0px 15px 15px;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 10px;
}

.compact-form .el-form-item {
  margin-bottom: 5px;
  margin-right: 5px;
}

.search-buttons {
  display: flex;
  gap: 5px;
}

.search-buttons-item {
  margin-right: 0;
}

/* 记录列表样式 */
.records-container {
  padding: 5px;
  overflow-x: hidden;
}

/* 表格样式优化 */
.el-table {
  width: 100% !important;
}

.el-table__body-wrapper {
  overflow-x: hidden;
}

/* 操作列下拉菜单样式 */
.el-dropdown {
  width: 100%;
}

.el-dropdown .el-button {
  width: 100%;
  text-align: center;
}

.el-dropdown-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.el-dropdown-menu__item i {
  margin-right: 0;
}

/* 分页容器样式 */
.pagination-container {
  margin-top: 15px;
  text-align: right;
}

/* 记录详情样式 */
.record-info-card {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 15px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.record-info-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 12px;
  gap: 15px;
}

.record-info-item {
  display: flex;
  align-items: center;
}

.label {
  color: #606266;
  font-size: 13px;
  margin-right: 5px;
  white-space: nowrap;
}

.value {
  color: #303133;
  font-size: 13px;
  font-weight: 500;
}

/* 记录消息样式 */
.record-message {
  padding: 10px 15px;
  background-color: #fff;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
  color: #303133;
  margin-bottom: 12px;
  border-left: 3px solid #409EFF;
}

/* 处理区域样式 */
.record-handle-area {
  margin-top: 10px;
}

.handle-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.handle-options {
  display: flex;
  gap: 15px;
  margin-right: 10px;
}

.handle-input {
  flex: 1;
  margin: 0 10px;
}

/* 已处理信息样式 */
.record-handled-info {
  margin-top: 10px;
}

.handle-info-row {
  display: flex;
  flex-wrap: wrap;
  background-color: #fff;
  padding: 10px;
  border-radius: 4px;
  gap: 20px;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-label {
  color: #606266;
  font-size: 13px;
  margin-right: 5px;
}

/* 表格样式优化 */
.alert-message-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* 辅助类 */
.el-divider {
  margin: 10px 0;
}
</style>
